import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import {
  CalendarIcon,
  RefreshCw,
  Eye,
  Camera,
  Check,
  AlertTriangle,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  History,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate, formatDateTime } from "@/lib/dates";
import DamageMarkerOverlay, { DamageMarker } from "./DamageMarkerOverlay";
import DamageHistoryTimeline from "./DamageHistoryTimeline";
import DamageReportButtons from "./DamageReportButtons";

type Inspection = {
  id: string;
  vehicle_number: string;
  driver_name: string;
  created_at: string;
  overall_status: string;
  notes: string;
  inspection_type: string | null;
};

type InspectionItem = {
  id: string;
  inspection_id: string;
  category: string;
  item_key: string;
  label: string;
  status: string;
  note: string;
  resolved_at: string | null;
  resolved_by: string | null;
};

type InspectionPhoto = {
  id: string;
  inspection_id: string;
  category: string;
  photo_type: string;
  storage_path: string;
};

type Vehicle = {
  vehicle_number: string;
  make?: string;
  model?: string;
};

const BUCKET_VEHICLE_INSPECTIONS = "vehicle-inspections";

const STATUS_COLORS: Record<string, string> = {
  OK: "text-green-400 border-green-400/50 bg-green-500/10",
  "Needs Attention": "text-yellow-400 border-yellow-400/50 bg-yellow-500/10",
  "Needs Fixing Soon": "text-orange-400 border-orange-400/50 bg-orange-500/10",
  Unsafe: "text-red-400 border-red-400/50 bg-red-500/10",
};

// A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
// sees inspection history only for their linked vehicle(s), and can only
// mark items OK / edit damage markers with edit rights on Fleet.
const InspectionHistoryView: React.FC<{ scopedVehicleNumbers?: string[] | null; canEdit?: boolean }> = ({
  scopedVehicleNumbers = null,
  canEdit = true,
}) => {
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [vehicleFilter, setVehicleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [inspectionItems, setInspectionItems] = useState<InspectionItem[]>([]);
  const [inspectionPhotos, setInspectionPhotos] = useState<InspectionPhoto[]>([]);
  const [damageMarkers, setDamageMarkers] = useState<Record<string, DamageMarker[]>>({});
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingMarkers, setSavingMarkers] = useState(false);
  const [markingOk, setMarkingOk] = useState<string | null>(null);

  // Photo viewer
  const [enlargedPhotoIndex, setEnlargedPhotoIndex] = useState<number | null>(null);

  // Damage history timeline modal
  const [damageTimelineOpen, setDamageTimelineOpen] = useState(false);
  const [damageTimelineVehicle, setDamageTimelineVehicle] = useState<string>("");


  const loadInspections = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("vehicle_inspections")
        .select("id, vehicle_number, driver_name, created_at, overall_status, notes, inspection_type")
        // Leave out an inspection that is still being worked on; it joins the history once finished.
        .or("completed_at.not.is.null,progress_pct.is.null")
        .order("created_at", { ascending: false })
        .limit(500);
      if (scopedVehicleNumbers) {
        query = query.in("vehicle_number", scopedVehicleNumbers.length > 0 ? scopedVehicleNumbers : ["__none__"]);
      }
      const { data, error } = await query;

      if (error) throw error;
      setInspections(data || []);
    } catch (e: any) {
      toast({
        title: "Error loading inspections",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      let query = supabase
        .from("vehicles")
        .select("vehicle_number, make, model")
        .order("vehicle_number");
      if (scopedVehicleNumbers) {
        query = query.in("vehicle_number", scopedVehicleNumbers.length > 0 ? scopedVehicleNumbers : ["__none__"]);
      }
      const { data, error } = await query;

      if (error) throw error;
      setVehicles(data || []);
    } catch (e: any) {
      console.error("Error loading vehicles:", e);
    }
  };

  useEffect(() => {
    loadInspections();
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedVehicleNumbers]);

  const filteredInspections = useMemo(() => {
    return inspections.filter((i) => {
      // Vehicle filter
      if (vehicleFilter !== "all" && i.vehicle_number !== vehicleFilter) return false;

      // Status filter
      if (statusFilter !== "all" && i.overall_status !== statusFilter) return false;

      // Type filter
      if (typeFilter !== "all") {
        const inspType = i.inspection_type || "driver";
        if (inspType !== typeFilter) return false;
      }

      // Date from filter
      if (dateFrom) {
        const inspDate = new Date(i.created_at);
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (inspDate < fromDate) return false;
      }

      // Date to filter
      if (dateTo) {
        const inspDate = new Date(i.created_at);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (inspDate > toDate) return false;
      }

      return true;
    });
  }, [inspections, vehicleFilter, statusFilter, typeFilter, dateFrom, dateTo]);

  const uniqueVehicles = useMemo(() => {
    const vehicleNums = new Set(inspections.map((i) => i.vehicle_number));
    return Array.from(vehicleNums).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
  }, [inspections]);

  const getPhotoUrl = (storagePath: string): string => {
    if (!storagePath) return "";
    const { data } = supabase.storage
      .from(BUCKET_VEHICLE_INSPECTIONS)
      .getPublicUrl(storagePath);
    return data?.publicUrl || "";
  };

  const openDetail = async (inspection: Inspection) => {
    setSelectedInspection(inspection);
    setDetailOpen(true);
    setLoadingDetail(true);

    try {
      // Load items
      const { data: itemsData, error: itemsError } = await supabase
        .from("vehicle_inspection_items")
        .select("id, inspection_id, category, item_key, label, status, note, resolved_at, resolved_by")
        .eq("inspection_id", inspection.id)
        .order("category");

      if (itemsError) throw itemsError;
      setInspectionItems(itemsData || []);

      // Load photos
      const { data: photosData, error: photosError } = await supabase
        .from("vehicle_inspection_photos")
        .select("id, inspection_id, category, photo_type, storage_path")
        .eq("inspection_id", inspection.id);

      if (photosError) throw photosError;
      setInspectionPhotos(photosData || []);

      // Load damage markers for each photo
      if (photosData && photosData.length > 0) {
        const photoIds = photosData.map((p) => p.id);
        const { data: markersData, error: markersError } = await supabase
          .from("vehicle_inspection_damage_markers")
          .select("*")
          .in("photo_id", photoIds);

        if (!markersError && markersData) {
          const markersByPhoto: Record<string, DamageMarker[]> = {};
          for (const marker of markersData) {
            if (!markersByPhoto[marker.photo_id]) {
              markersByPhoto[marker.photo_id] = [];
            }
            markersByPhoto[marker.photo_id].push({
              id: marker.id,
              x_percent: marker.x_percent,
              y_percent: marker.y_percent,
              damage_type: marker.damage_type,
              severity: marker.severity,
              notes: marker.notes || "",
            });
          }
          setDamageMarkers(markersByPhoto);
        }
      }
    } catch (e: any) {
      toast({
        title: "Error loading details",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleMarkItemOk = async (item: InspectionItem) => {
    if (!canEdit) return;
    setMarkingOk(item.id);
    try {
      const driverName = localStorage.getItem("driver_name") || "Admin";
      const now = new Date().toISOString();

      const { error } = await supabase
        .from("vehicle_inspection_items")
        .update({
          status: "OK",
          resolved_at: now,
          resolved_by: driverName,
        })
        .eq("id", item.id);

      if (error) throw error;

      const updatedItems = inspectionItems.map((i) =>
        i.id === item.id ? { ...i, status: "OK", resolved_at: now, resolved_by: driverName } : i
      );
      setInspectionItems(updatedItems);

      // Check if all items are now OK and update overall status
      const allOk = updatedItems.every((i) => i.status === "OK");
      if (allOk && selectedInspection) {
        await supabase
          .from("vehicle_inspections")
          .update({ overall_status: "OK" })
          .eq("id", selectedInspection.id);

        setSelectedInspection((prev) => (prev ? { ...prev, overall_status: "OK" } : prev));
        setInspections((prev) =>
          prev.map((i) => (i.id === selectedInspection.id ? { ...i, overall_status: "OK" } : i))
        );
      }

      toast({ title: "Item marked as OK" });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Unknown error";
      toast({ title: "Error updating item", description: message, variant: "destructive" });
    } finally {
      setMarkingOk(null);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedInspection(null);
    setInspectionItems([]);
    setInspectionPhotos([]);
    setDamageMarkers({});
    setEnlargedPhotoIndex(null);
  };

  const clearFilters = () => {
    setVehicleFilter("all");
    setStatusFilter("all");
    setTypeFilter("all");
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  // Handle damage marker changes for a specific photo
  const handleMarkersChange = async (photoId: string, newMarkers: DamageMarker[]) => {
    if (!canEdit) return;
    const existingMarkers = damageMarkers[photoId] || [];
    
    // Update local state immediately for responsive UI
    setDamageMarkers((prev) => ({
      ...prev,
      [photoId]: newMarkers,
    }));

    setSavingMarkers(true);
    try {
      // Find markers to delete (exist in old but not in new)
      const markersToDelete = existingMarkers.filter(
        (em) => em.id && !newMarkers.find((nm) => nm.id === em.id)
      );

      // Find markers to add (exist in new but have no id)
      const markersToAdd = newMarkers.filter((nm) => !nm.id);

      // Delete removed markers
      if (markersToDelete.length > 0) {
        const idsToDelete = markersToDelete.map((m) => m.id).filter(Boolean);
        const { error: deleteError } = await supabase
          .from("vehicle_inspection_damage_markers")
          .delete()
          .in("id", idsToDelete);

        if (deleteError) throw deleteError;
      }

      // Add new markers
      if (markersToAdd.length > 0) {
        // Get the inspection_id from the photo
        const photo = inspectionPhotos.find(p => p.id === photoId);
        const inspectionId = photo?.inspection_id || selectedInspection?.id;
        
        if (!inspectionId) {
          throw new Error("Could not determine inspection ID for this photo");
        }
        
        const insertData = markersToAdd.map((marker) => ({
          photo_id: photoId,
          inspection_id: inspectionId,
          x_percent: marker.x_percent,
          y_percent: marker.y_percent,
          damage_type: marker.damage_type,
          severity: marker.severity,
          notes: marker.notes || null,
        }));

        const { data: insertedData, error: insertError } = await supabase
          .from("vehicle_inspection_damage_markers")
          .insert(insertData)
          .select();


        if (insertError) throw insertError;

        // Update local state with the new IDs
        if (insertedData) {
          const updatedMarkers = newMarkers.map((nm) => {
            if (!nm.id) {
              // Find the matching inserted marker
              const inserted = insertedData.find(
                (im) =>
                  im.x_percent === nm.x_percent &&
                  im.y_percent === nm.y_percent &&
                  im.damage_type === nm.damage_type
              );
              if (inserted) {
                return { ...nm, id: inserted.id };
              }
            }
            return nm;
          });

          setDamageMarkers((prev) => ({
            ...prev,
            [photoId]: updatedMarkers,
          }));
        }
      }

      toast({
        title: "Damage markers saved",
        description: "The damage markers have been updated successfully.",
      });
    } catch (e: any) {
      toast({
        title: "Error saving markers",
        description: e.message,
        variant: "destructive",
      });
      // Revert to previous state on error
      setDamageMarkers((prev) => ({
        ...prev,
        [photoId]: existingMarkers,
      }));
    } finally {
      setSavingMarkers(false);
    }
  };

  const hasActiveFilters =
    vehicleFilter !== "all" ||
    statusFilter !== "all" ||
    typeFilter !== "all" ||
    dateFrom !== undefined ||
    dateTo !== undefined;

  // Group items by category
  const itemsByCategory = useMemo(() => {
    const grouped: Record<string, InspectionItem[]> = {};
    for (const item of inspectionItems) {
      if (!grouped[item.category]) grouped[item.category] = [];
      grouped[item.category].push(item);
    }
    return grouped;
  }, [inspectionItems]);

  const categoryOrder = [
    "Fluids",
    "Oil",
    "Brakes",
    "Camera",
    "Lights",
    "Tyres",
    "Interior",
    "Exterior",
    "Safety",
    "Signage",
    "Hardware",
    "Setup Inspection",
  ];

  const sortedCategories = Object.keys(itemsByCategory).sort((a, b) => {
    const aIdx = categoryOrder.indexOf(a);
    const bIdx = categoryOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  const currentPhoto = enlargedPhotoIndex !== null ? inspectionPhotos[enlargedPhotoIndex] : null;


  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Inspection History
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <DamageReportButtons vehicleNumber={vehicleFilter !== "all" ? vehicleFilter : null} dateFrom={dateFrom} dateTo={dateTo} />
            <Button 
              variant="outline" 
              onClick={() => {
                setDamageTimelineVehicle("");
                setDamageTimelineOpen(true);
              }}
            >
              <History className="w-4 h-4 mr-2" />
              Damage Timeline
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
            <Button variant="outline" onClick={loadInspections} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 p-4 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            {/* Vehicle filter */}
            <div className="w-[160px]">
              <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vehicles</SelectItem>
                  {uniqueVehicles.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status filter */}
            <div className="w-[160px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="OK">OK</SelectItem>
                  <SelectItem value="Needs Attention">Needs Attention</SelectItem>
                  <SelectItem value="Needs Fixing Soon">Needs Fixing Soon</SelectItem>
                  <SelectItem value="Unsafe">Unsafe</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Type filter */}
            <div className="w-[140px]">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="driver">Driver</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date from */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[140px] justify-start text-left font-normal",
                    !dateFrom && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                  {dateFrom ? format(dateFrom, "dd/MM/yy") : "From"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Date to */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[140px] justify-start text-left font-normal",
                    !dateTo && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                  {dateTo ? format(dateTo, "dd/MM/yy") : "To"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredInspections.length} of {inspections.length} inspections
          </div>

          {/* Table */}
          <div className="overflow-auto max-h-[500px] border rounded-lg">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>Date/Time</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Completed By</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInspections.map((inspection) => (
                  <TableRow key={inspection.id}>
                    <TableCell className="text-sm">
                      {formatDateTime(inspection.created_at)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {inspection.vehicle_number}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {inspection.inspection_type === "admin" ? "Admin" : "Driver"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inspection.driver_name || "-"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn("text-xs", STATUS_COLORS[inspection.overall_status])}
                      >
                        {inspection.overall_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetail(inspection)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {filteredInspections.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {loading ? "Loading..." : "No inspections found matching your filters."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Damage History Timeline Modal */}
      <DamageHistoryTimeline
        isOpen={damageTimelineOpen}
        onClose={() => setDamageTimelineOpen(false)}
        vehicleNumber={damageTimelineVehicle}
        canEdit={canEdit}
      />


      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-amber-500 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Inspection Details
              {selectedInspection && (
                <Badge variant="outline" className="ml-2">
                  {selectedInspection.vehicle_number}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {loadingDetail ? (
            <div className="py-8 text-center text-muted-foreground">Loading details...</div>
          ) : selectedInspection ? (
            <div className="space-y-6">
              {/* Header info */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge
                      variant="outline"
                      className={cn(STATUS_COLORS[selectedInspection.overall_status])}
                    >
                      {selectedInspection.overall_status}
                    </Badge>
                    <Badge variant="outline">
                      {selectedInspection.inspection_type === "admin" ? "Admin Inspection" : "Driver Inspection"}
                    </Badge>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {formatDateTime(selectedInspection.created_at)}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Completed by: </span>
                  <span className="font-medium">{selectedInspection.driver_name || "Unknown"}</span>
                </div>
                {selectedInspection.notes && (
                  <div className="text-sm border-t border-gray-700 pt-2 mt-2">
                    <span className="text-muted-foreground">Notes: </span>
                    <span>{selectedInspection.notes}</span>
                  </div>
                )}
              </div>

              {/* Photos with damage markers - Admins can add damage markers */}
              {inspectionPhotos.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Photos ({inspectionPhotos.length})
                    </div>
                    {savingMarkers && (
                      <span className="text-xs text-amber-500 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Saving...
                      </span>
                    )}
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-400">
                    <strong>Tip:</strong> Click "Add Damage" on any photo to mark damage areas. Markers are saved automatically.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {inspectionPhotos.map((photo) => (
                      <DamageMarkerOverlay
                        key={photo.id}
                        imageUrl={getPhotoUrl(photo.storage_path)}
                        photoType={photo.photo_type}
                        markers={damageMarkers[photo.id] || []}
                        onMarkersChange={(newMarkers) => handleMarkersChange(photo.id, newMarkers)}
                        readOnly={false}
                      />
                    ))}
                  </div>
                </div>
              )}


              {/* Checklist items by category */}
              {inspectionItems.length > 0 && (
                <div className="space-y-4">
                  <div className="text-sm font-semibold">
                    Checklist Items ({inspectionItems.length})
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {sortedCategories.map((category) => (
                      <div
                        key={category}
                        className="rounded-lg border border-gray-700/60 bg-gray-900/30 p-3"
                      >
                        <div className="text-xs font-semibold text-amber-500 mb-2 uppercase tracking-wide">
                          {category}
                        </div>
                        <div className="space-y-1">
                          {itemsByCategory[category].map((item) => (
                            <div
                              key={item.id}
                              className={cn(
                                "rounded-lg p-2 border",
                                STATUS_COLORS[item.status] || "bg-muted/30"
                              )}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  {item.status === "OK" ? (
                                    <Check className="w-3 h-3 text-green-400 shrink-0" />
                                  ) : (
                                    <AlertTriangle
                                      className={`w-3 h-3 shrink-0 ${
                                        STATUS_COLORS[item.status]?.split(" ")[0] || "text-yellow-400"
                                      }`}
                                    />
                                  )}
                                  <span className="text-sm truncate">{item.label}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <Badge
                                    variant="outline"
                                    className={cn("text-xs", STATUS_COLORS[item.status])}
                                  >
                                    {item.status}
                                  </Badge>
                                  {item.status !== "OK" && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-green-400 border-green-400/50 hover:bg-green-400/10"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkItemOk(item);
                                      }}
                                      disabled={markingOk === item.id}
                                    >
                                      {markingOk === item.id ? (
                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <>
                                          <Check className="w-3 h-3 mr-1" />
                                          Mark OK
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                              {item.note && (
                                <div className="text-xs text-muted-foreground mt-1 pl-5">
                                  Note: {item.note}
                                </div>
                              )}
                              {item.resolved_at && (
                                <div className="text-xs text-green-400 mt-1 pl-5">
                                  Resolved {formatDate(item.resolved_at)}
                                  {item.resolved_by && ` by ${item.resolved_by}`}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              {inspectionItems.length > 0 && (
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Total items checked:</span>
                    <span className="font-medium">{inspectionItems.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Items OK:</span>
                    <span className="font-medium text-green-400">
                      {inspectionItems.filter((i) => i.status === "OK").length}
                    </span>
                  </div>
                  {inspectionItems.filter((i) => i.status !== "OK").length > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Items needing attention:</span>
                      <span className="font-medium text-yellow-400">
                        {inspectionItems.filter((i) => i.status !== "OK").length}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InspectionHistoryView;
